<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">{{ $t('settings.settingsLabel') }}</span>
        <h3 class="page-title">{{ $t('settings.emailTemplates.editEmailTemplate') }}</h3>
      </d-col>
      <d-col col sm="6" class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="secondary"
          @click.prevent="$router.push('/ustawienia/szablony-email')"
          class="mr-1">
          <i class="material-icons">arrow_back</i>
          {{ $t('ui.backToList') }}
        </d-button>
        <d-button theme="success" @click.prevent="validateForm">
          <i class="material-icons">edit</i>
          {{ $t('settings.emailTemplates.editEmailTemplate') }}
        </d-button>
      </d-col>
    </d-row>

    <!-- Form -->
    <d-form :class="{ 'is-loading': !allDataLoaded }">
      <d-card class="mb-5">
        <d-tabs card>
          <!-- Dane ogólne -->
          <d-tab
            :title="$t('ui.generalInfo')"
            active>
            <h4 class="mb-3 pb-3 border-bottom">
              {{ $t('ui.generalInfo') }}
            </h4>

            <validation-observer ref="email-template-new-form" tag="form">
              <div class="form-group">
                <label for="name">
                  {{ $t('settings.emailTemplates.language') }}:
                </label>

                <validation-provider
                  rules="required"
                  name="language-id"
                  v-slot="{ errors }">
                  <d-form-select
                    v-model="language_id"
                    :state="errors.length ? 'invalid' : null"
                    :options="languagesOptions" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label>
                  {{ $t('settings.emailTemplates.footerImage') }}:
                </label>

                <div v-if="footer_image_url">
                  <img :src="footer_image_url" style="max-width: 100%; height: auto;" />
                </div>

                <div v-if="footer_image_url">
                  <a
                    href="#"
                    @click.prevent="removeFooterImage()"
                    class="text-danger">
                    <small>{{ $t('image.removePhoto') }}</small>
                  </a>
                </div>

                <file-input
                  v-if="!footer_image_url"
                  :onChange="setFooterImage"
                  id="inputFileFooterImage" />
              </div>

              <div class="form-group">
                <label>
                  {{ $t('settings.emailTemplates.footerUrl') }}:
                </label>

                <validation-provider
                  rules="valid-url"
                  name="footer-url"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    v-model="footer_url" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label>
                  {{ $t('settings.emailTemplates.companyName') }}:
                </label>

                <d-input
                  v-model="company_name" />
              </div>

              <div class="form-group">
                <label>
                  {{ $t('settings.emailTemplates.address1') }}:
                </label>

                <d-input
                  v-model="address_1" />
              </div>

              <div class="form-group">
                <label>
                  {{ $t('settings.emailTemplates.address2') }}:
                </label>

                <d-input
                  v-model="address_2" />
              </div>

              <div class="form-group">
                <label>
                  {{ $t('settings.emailTemplates.url') }}:
                </label>

                <validation-provider
                  rules="valid-url"
                  name="url"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    v-model="url" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label>
                  {{ $t('settings.emailTemplates.email') }}:
                </label>

                <validation-provider
                  rules="email"
                  name="email"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    v-model="email" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label>
                  {{ $t('settings.emailTemplates.phone') }}:
                </label>

                <d-input
                  v-model="phone" />
              </div>

              <div class="form-group">
                <label>
                  {{ $t('settings.emailTemplates.facebookUrl') }}:
                </label>

                <validation-provider
                  rules="valid-url"
                  name="facebook-url"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    v-model="facebook_url" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label>
                  {{ $t('settings.emailTemplates.youtubeUrl') }}:
                </label>

                <validation-provider
                  rules="valid-url"
                  name="youtube-url"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    v-model="youtube_url" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label>
                  {{ $t('settings.emailTemplates.linkedinUrl') }}:
                </label>

                <validation-provider
                  rules="valid-url"
                  name="linkedin-url"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    v-model="linkedin_url" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label>
                  {{ $t('settings.emailTemplates.instagramUrl') }}:
                </label>

                <validation-provider
                  rules="valid-url"
                  name="facebook-url"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    v-model="instagram_url" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>
            </validation-observer>
          </d-tab>
        </d-tabs>
      </d-card>
    </d-form>
  </d-container>
</template>

<script>
import FormUtils from './../../utils/FormUtils.js';

export default {
  name: 'email-template-new',
  computed: {
    allDataLoaded () {
      return this.loadedLanguages && this.loadedData;
    },
    languagesOptions () {
      return [{ value: '', text: this.$t('settings.language.chooseLanguage') }].concat(this.languages.map(language => ({
        value: language.id,
        text: language.name
      })));
    }
  },
  data() {
    return {
      id: 0,
      language_id: '',
      footer_image_url: '',
      footer_image: '',
      footer_url: '',
      company_name: '',
      address_1: '',
      address_2: '',
      url: '',
      email: '',
      phone: '',
      facebook_url: '',
      youtube_url: '',
      linkedin_url: '',
      instagram_url: '',
      // helper fields
      loadedLanguages: false,
      languages: [],
      loadedData: false,
      loadError: false
    };
  },
  mounted () {
    this.loadAdditionalData();

    FormUtils.loadItemData(this, {
      method: 'get',
      endpointBase: '/email-templates/item/',
      instanceFields: ['id', 'language_id', 'footer_image_url', 'footer_url', 'company_name', 'address_1', 'address_2', 'url', 'email', 'phone', 'facebook_url', 'youtube_url', 'linkedin_url', 'instagram_url'],
      loadedKey: 'loadedData',
      errorKey: 'loadError'
    });
  },
  methods: {
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/languages/items',
        loadedKey: 'loadedLanguages',
        outputKey: 'languages',
        errorKey: 'loadError'
      });
    },
    setFooterImage (event) {
      this.footerImage = event.target.files[0];
    },
    removeFooterImage () {
      this.footer_image_url = '';
      this.footer_image = '';
    },
    validateForm () {
      FormUtils.validate(this.$refs['email-template-new-form'], this.submitForm);
    },
    submitForm () {
      let imageFields = false;

      let formDataToSend = {
        language_id: this.language_id,
        footer_url: this.footer_url,
        company_name: this.company_name,
        address_1: this.address_1,
        address_2: this.address_2,
        url: this.url,
        email: this.email,
        phone: this.phone,
        facebook_url: this.facebook_url,
        youtube_url: this.youtube_url,
        linkedin_url: this.linkedin_url,
        instagram_url: this.instagram_url
      };

      if (this.footerImage) {
        formDataToSend.footer_image = this.footerImage;
        imageFields = ['footer_image'];
      }

      FormUtils.submit({
        $http: this.$http,
        endpoint: '/email-templates/update/' + this.id,
        formData: formDataToSend,
        successTitle: this.$t('settings.emailTemplates.editEmailTemplateSuccessTitle'),
        successText: this.$t('settings.emailTemplates.editEmailTemplateSuccessText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('settings.emailTemplates.editEmailTemplateErrorText'),
        successAction: () => {
          this.$router.push('/ustawienia/szablony-email')
        }
      }, imageFields);
    }
  }
};
</script>

<style lang="scss" scoped>
form.is-loading {
  opacity: .25;
  pointer-events: none;
}
</style>
